import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSession } from '@/contexts/session-context';
import type { OverflowMode } from '@/types/billing';
import { toast } from 'sonner';
import type { WorkspacePublicId, WorkspaceKind } from '@hookmyapp/shared';
import { isValidPublicId } from '@/lib/public-id';
import type { Workspace } from '@/types/workspace';
import type { Channel } from '@/types/channel';
import { triggerErrorPage } from '@/contexts/error-context';
import { captureException, captureFrontendError } from '@/lib/sentry';
import { usePostHog } from '@posthog/react';
import { UnexpectedError } from '@hookmyapp/observability/errors';
import { resolveFirstTouchSource } from '@/lib/first-touch';
import { peekHandoffJwt } from '@/lib/handoff-storage';
import { useDemoHandoffClaim } from '@/hooks/use-demo-handoff-claim';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQueryWithTransport } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

const ACTIVE_WORKSPACE_KEY = 'active_workspace';

// Stable empty channels reference so a channels-less render doesn't churn the
// context value memo (a fresh `[]` literal each render would).
const EMPTY_CHANNELS: Channel[] = [];

/** Raw row shape returned by GET /workspaces. */
interface WorkspaceRow {
  id: string;
  name: string;
  role: 'admin' | 'member';
  organizationPublicId: string;
  organizationName: string;
  kind: unknown;
}

// Spec fail-safe: unknown/unparseable kind becomes null and emits a warning.
// Never coerce null to 'team' — a malformed customer would leak into the
// Workspaces list (which filters by exact === 'team').
function parseKindOrWarn(raw: unknown, wsId: string): WorkspaceKind | null {
  // Inline fail-safe parse (mirrors @hookmyapp/shared asWorkspaceKind). NOT
  // imported from the shared package: its barrel pulls @hookmyapp/observability
  // (Sentry/Node) into the browser bundle and crashes the app with
  // `util.inherits is not a function`, and shared exposes no browser-safe
  // runtime subpath. The kind union is exactly two values (spec); this behavior
  // is covered by workspace-context-kind.test.ts.
  const k: WorkspaceKind | null =
    raw === 'team' || raw === 'customer' ? raw : null;
  if (k === null) {
    // eslint-disable-next-line no-console -- intentional fail-safe signal
    console.warn(`workspace ${wsId} has unrecognized kind`, { kind: raw });
  }
  return k;
}

/** Exported for unit testing. Maps a raw /workspaces row to a Workspace. */
export function mapWorkspaceRow(row: WorkspaceRow): Workspace {
  return {
    id: row.id as Workspace['id'],
    name: row.name,
    role: row.role,
    organizationPublicId: row.organizationPublicId,
    organizationName: row.organizationName,
    // ponytail: no 'team' fallback; null renders in neither presentation list
    kind: parseKindOrWarn(row.kind, row.id),
  };
}

/**
 * Phase 117: read the active workspace id from localStorage with a format
 * gate. Pre-117 dev devices may have a raw UUID here — validate against
 * the `ws_` publicId shape, silently clear any stale value, and return
 * null so the provider falls through to the first-workspace default.
 * NOT a backfill — this is a read-time validator. Users never see an
 * error; the stale value is quietly dropped.
 */
function readStoredWorkspaceId(): WorkspacePublicId | null {
  const stored = localStorage.getItem(ACTIVE_WORKSPACE_KEY);
  if (stored === null) return null;
  if (!isValidPublicId(stored, 'ws')) {
    localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    return null;
  }
  return stored as WorkspacePublicId;
}

const API_URL = import.meta.env.VITE_API_URL ?? '';

// Threshold percentages at which a warning toast fires.
// Defined at module level so it is stable across renders and never needs to
// appear in useEffect dependency arrays.
const THRESHOLDS = [50, 75, 80, 90] as const;

/** Org-pool usage shape returned by GET /organizations/:orgId/usage (spec D5). */
interface OrgUsageData {
  total: number;
  limit: number;
  percentage: number;
  /** AIT-629: current quota window, for the dashboards' billing-period range
   *  and renewal date. Absent on a pre-AIT-629 backend. */
  resetsAt?: string;
  periodStartsAt?: string;
  /** AIT-420: the meter these numbers are in. Absent on a pre-v2 backend. */
  usageUnit?: 'messages' | 'actions';
  /** AIT-420: kept for wire back-compat; always false now. */
  unlimited?: boolean;
  /** AIT-420 Task 8: days left in an active Stripe trial (status 'trialing');
   *  null once converted, lapsed, or never trialed. Same field the queries.ts
   *  OrgUsage type carries — the header chip reads whichever of the two
   *  resolves first (context vs a direct query), so both must match. */
  trialDaysLeft?: number | null;
}

export interface UsageData {
  /** Usage this quota period, in `usageUnit` (AIT-436). One number: the
   *  direction split and the totalMessages/totalForwards aliases are gone. */
  total: number;
  limit: number;
  percentage: number;
  /** Which meter every number here is in. */
  usageUnit: 'messages' | 'actions';
  channels: Array<{
    channelId: string;
    name: string;
    type: string;
    total: number;
    percentage: number;
  }>;
  // --- two-bucket billing view (spec §5) ---
  planQuota: number;
  reserveBalance: number;
  reserveExpiresAt: string | null;
  /** AIT-593: org-wide "delivery is stopped". Absent on a pre-AIT-593 backend. */
  channelsPaused?: boolean;
  overflowMode: OverflowMode;
}

/** Distinct org the user can act in, derived from the cross-org workspace union. */
export interface OrgOption {
  publicId: string;
  name: string;
}

interface WorkspaceContextValue {
  activeWorkspace: Workspace | null;
  workspaces: Workspace[];
  /**
   * The FULL cross-org union, unfiltered by session org (AIT-539). `workspaces`
   * above is the session-org subset — the right list for switchers and route
   * validation. This one exists so a view handed a workspace outside the
   * session org can tell "not yours" from "yours, wrong scope" and re-scope
   * instead of bouncing. Do not use it to render or to act on a workspace.
   */
  crossOrgWorkspaces: Workspace[];
  /** Every org the user can act in (one entry per distinct org), for the switcher. */
  organizations: OrgOption[];
  channels: Channel[];
  usage: UsageData | null;
  /** Org-pooled usage (total messages this quota period across the org vs plan quota). */
  orgUsage: OrgUsageData | null;
  sessionScoped: boolean;
  switchWorkspace: (id: WorkspacePublicId) => void;
  refreshChannels: (workspaceId?: WorkspacePublicId) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  loading: boolean;
  channelsLoading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const {
    organizationId,
    user,
    isLoading: authLoading,
  } = useSession();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState<WorkspacePublicId | null>(() =>
    readStoredWorkspaceId(),
  );
  // Phase 118: WorkspaceProvider now mounts under `/` (for RootRedirect) as
  // well as under /w/:ws/*. Unauthed users hit `/` — in that case we have
  // nothing to fetch and `loading` stays `false` so RootRedirect can render
  // LandingPage immediately without waiting on a 401'd /workspaces fetch.
  const [loading, setLoading] = useState(true);
  const lastNotifiedThreshold = useRef<number>(0);
  // AIT-547: last activeId we dropped on a 403, so the recovery below runs
  // at most once per workspace instead of looping.
  const resetForId = useRef<WorkspacePublicId | null>(null);

  // Internal fetch helper to avoid circular dependency with useFetch.
  //
  // Phase B sealed-session migration — cookie auth via credentials:'include'.
  // The `/workspaces` request is the FIRST authed call after fresh signup
  // and still carries the demo→signup handoff JWT as `X-Handoff-JWT` so the
  // backend's `applyHandoffIfPresent` runs inside bootstrap/runSignupWithUser.
  // PEEK rather than consume so the claim effect can still see the JWT after
  // authFetch fires.
  const authFetch = useCallback(
    async (path: string, extraHeaders?: Record<string, string>) => {
      const handoffJwt = peekHandoffJwt();
      return fetch(`${API_URL}${path}`, {
        credentials: 'include',
        headers: {
          ...(extraHeaders ?? {}),
          ...(handoffJwt && { 'X-Handoff-JWT': handoffJwt }),
        },
      });
    },
    [],
  );

  // Fetch workspaces on mount
  const fetchWorkspaces = useCallback(async () => {
    try {
      const res = await authFetch('/workspaces');
      if (res.ok) {
        const rows: WorkspaceRow[] = await res.json();
        const data = rows.map(mapWorkspaceRow);
        setWorkspaces(data);
        return data;
      }
      if (res.status >= 500) {
        captureException(new Error('Workspace fetch failed'), { status: res.status });
        triggerErrorPage({ message: 'Server error', url: '/workspaces', status: res.status });
      }
    } catch (error) {
      // fetch TypeError = transient transport failure (network blip,
      // navigation-aborted request) → sev2; AIT-335: these were paging SEV1
      // while the API was healthy. Anything else here (malformed 2xx body,
      // mapping throw) is a real data problem and stays sev1.
      if (error instanceof TypeError) {
        captureFrontendError(error, { context: 'fetchWorkspaces' });
      } else {
        captureException(error, { context: 'fetchWorkspaces' });
      }
      triggerErrorPage({ message: 'Network error', url: '/workspaces' });
    }
    return [];
  }, [authFetch]);

  // Initial load — only fetch /workspaces if there's a user. For unauthed
  // sessions (Phase 118: landing at `/`), skip the fetch entirely and flip
  // loading=false so RootRedirect can render LandingPage.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Unauthed at `/`: nothing to fetch, flip loading so RootRedirect renders.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- terminal loading flip, not a render cascade
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      await fetchWorkspaces();
      if (cancelled) return;
      // Active-workspace resolution moved to the org-scoped reconcile effect
      // below — it must validate against the CURRENT org's workspaces, not the
      // cross-org union returned here.
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // Fire once auth resolves (either anonymously or with a user).
  }, [authLoading, user?.id]);

  // SaaS multi-org: /workspaces returns the cross-org union (every org the
  // user belongs to), but the session is scoped to ONE org at a time. The
  // switcher, route validation, and active-workspace resolution must all see
  // only the current org's workspaces — otherwise a stale active workspace
  // from another org strands the user on cross-org 403s (the exact failure a
  // two-org admin hit at the WorkOS org picker). The full union stays in
  // `workspaces` state so an org switch re-filters instantly, no refetch.
  // organizationId from useSession is the org_ publicId (AIT-182 — /auth/me
  // stopped exposing the internal WorkOS org id).
  const orgWorkspaces = useMemo(
    () =>
      organizationId
        ? workspaces.filter((w) => w.organizationPublicId === organizationId)
        : [],
    [workspaces, organizationId],
  );

  const activeWorkspace = useMemo(
    () => orgWorkspaces.find((w) => w.id === activeId) ?? null,
    [orgWorkspaces, activeId],
  );

  // Distinct orgs the user can act in, derived from the FULL cross-org union
  // (not the org-scoped subset). Powers the OrgSwitcher with no extra fetch —
  // /workspaces is already loaded and universally mocked in tests.
  const organizations = useMemo<OrgOption[]>(() => {
    const seen = new Map<string, OrgOption>();
    for (const w of workspaces) {
      if (seen.has(w.organizationPublicId)) continue;
      seen.set(w.organizationPublicId, {
        publicId: w.organizationPublicId,
        name: w.organizationName,
      });
    }
    return [...seen.values()];
  }, [workspaces]);

  // Reconcile the active workspace against the current org. Runs on initial
  // load (stale localStorage from a prior session/org), on org switch
  // (organizationId flips → orgWorkspaces re-derives), and after a refetch.
  // If the stored active id isn't in the current org, fall back to that org's
  // first workspace and persist it.
  useEffect(() => {
    if (orgWorkspaces.length === 0) return;
    if (activeId && orgWorkspaces.some((w) => w.id === activeId)) return;
    // Prefer team workspace as default landing when no valid active workspace is set.
    const next = (orgWorkspaces.find((w) => w.kind === 'team') ?? orgWorkspaces[0]).id;
    // Reconcile the active workspace to the current org + persist it. Must be an
    // effect (it writes localStorage and depends on the derived orgWorkspaces).
    // eslint-disable-next-line react-hooks/set-state-in-effect -- derived-state reconcile with localStorage persist
    setActiveId(next);
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, next);
  }, [orgWorkspaces, activeId]);

  // Identify the user in PostHog once their profile is known.
  //
  // posthog-01 — distinct_id is the canonical WorkOS person id (user.workosId),
  // matching the server + CLI identify surfaces. `plan` was dropped from
  // $set — plan now lives ONLY on the organization group (spec Decision 1/3),
  // not the person profile.
  //
  // Phase 125-06 — the identify call carries $set + $set_once so the person
  // profile has (CONTEXT §15):
  //   $set.email      — overwrites every identify (email can change)
  //   $set_once.signup_date   — first write wins (PostHog semantics)
  //   $set_once.signup_source — resolved bucket from first-touch
  //
  // NOTE ON $set_once.signup_date: `$set_once` is idempotent by PostHog
  // design (first write wins), so `new Date().toISOString()` on the first
  // identify yields a single source of truth. Subsequent identifies pass
  // the same key but PostHog silently discards the write.
  useEffect(() => {
    if (!user) return;
    posthog?.identify(user.workosId, {
      email: user.email,
      name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
      $set: {
        email: user.email,
      },
      $set_once: {
        signup_date: new Date().toISOString(),
        signup_source: resolveFirstTouchSource(),
      },
    });
  }, [posthog, user?.workosId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 113 — attach workspace group to the current distinct_id so every
  // subsequent capture call auto-inherits $groups.workspace = ws_...
  // Must run AFTER posthog.identify() (the effect above) — otherwise group
  // associates with the anonymous id and the merge on identify misses.
  //
  // RULE-4 DEVIATION from 113-05-PLAN.md: the plan's <interfaces> block
  // referenced fabricated Workspace.publicId / Workspace.plan /
  // Workspace.hasConnectedWaba fields, none of which existed on the DTO at
  // the time. The DTO has since grown `organizationPublicId` (P6 Task 9,
  // consumed by the organization-group effect below) — the workspace group
  // properties stay name-only; `plan` still isn't a workspace-level field
  // (it's an organization-level group property, see spec Decision 3).
  useEffect(() => {
    if (!activeWorkspace?.id) return;
    posthog?.group('workspace', activeWorkspace.id, {
      name: activeWorkspace.name,
    });
  }, [posthog, activeWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // posthog-01 — attach the organization group so browser events join the org
  // group the nightly snapshot writes plan/mrr to. Key MUST be the HookMyApp
  // Organization.publicId (org_…) — NEVER workosOrganizationId; a mismatched
  // key silently splits org analytics into a second, property-less group
  // (spec Decision 2, Codex review catch).
  useEffect(() => {
    const orgPublicId = activeWorkspace?.organizationPublicId;
    if (!orgPublicId) return;
    posthog?.group('organization', orgPublicId);
  }, [posthog, activeWorkspace?.organizationPublicId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2 auth re-rooting: the session is org-scoped at login. All
  // workspaces belonging to the same organization are accessible without a
  // per-workspace re-seal. `sessionScoped` is true as long as an active
  // workspace exists AND the session carries a non-null organizationId.
  // No POST /auth/switch-organization is triggered on workspace switch.
  const sessionScoped = !!activeWorkspace && organizationId !== null;

  // Batch E (D8 adapter): channels/usage/orgUsage now flow through TanStack
  // Query so a mutation's invalidateQueries reaches these context consumers
  // (the cross-screen staleness the migration set out to fix). These run INSIDE
  // WorkspaceProvider, so they MUST use useApiQueryWithTransport with the
  // provider's own authFetch — never useApiQuery (that calls
  // useFetch→useWorkspace, a provider-before-provider crash). X-Workspace-Id is
  // passed per read exactly as the old imperative fetchChannels/fetchUsage did.
  // Gated on `activeId && sessionScoped`: firing earlier 403s against
  // WorkspaceGuard.
  const wsTransport = useCallback(
    (path: string) => authFetch(path, { 'X-Workspace-Id': activeId ?? '' }),
    [authFetch, activeId],
  );
  // orgUsage sends no workspace header; adapt authFetch's (path, headers)
  // signature to the Transport (path, init) shape.
  const orgTransport = useCallback((path: string) => authFetch(path), [authFetch]);

  const channelsQuery = useApiQueryWithTransport<Channel[]>(
    qk.workspace(activeId ?? '', 'channels'),
    '/meta/channels',
    wsTransport,
    { enabled: !!activeId && sessionScoped },
  );
  const channels = channelsQuery.data ?? EMPTY_CHANNELS;
  const channelsLoading = channelsQuery.isFetching;

  // Preserve the imperative fetchChannels error contract: 403 → drop the stale
  // active workspace (cross-org guard recovery), 5xx → global error page,
  // network failure → error page. Query holds the thrown AppError; this effect
  // replays those side effects. (throwForResponse sets details.status; a
  // transport/network failure is a UserBlockingError with no status.)
  useEffect(() => {
    const err = channelsQuery.error as { details?: { status?: number } } | null;
    if (!err) {
      // Cleared error = the workspace answered. Re-arm the one-shot recovery so
      // a LATER 403 episode (access revoked again after being restored) still
      // gets its fallback; the guard only suppresses a loop within one episode.
      resetForId.current = null;
      return;
    }
    const status = err.details?.status;
    if (status === 403) {
      // Cross-org guard: the stored active workspace isn't in this org — drop it
      // so the reconcile effect re-picks a valid one.
      //
      // AIT-547: only ONCE per id. When the 403 is not staleness (a broken
      // session claim 403s every workspace), clearing makes the reconcile
      // effect re-pick the same workspace, which 403s again — an unbounded
      // request loop against the API. One recovery attempt, then stop.
      if (resetForId.current === activeId) return;
      resetForId.current = activeId;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- error-recovery reset, not a render cascade
      setActiveId(null);
      localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    } else if (status && status >= 500) {
      captureException(new Error('Channels fetch failed'), { status });
      triggerErrorPage({ message: 'Server error', url: '/meta/channels', status });
    } else if (status === undefined) {
      // No HTTP status = the request never got a response (network blip,
      // aborted fetch). Sev2 — see AIT-335.
      captureFrontendError(new Error('Channels fetch failed'), { context: 'fetchChannels' });
      triggerErrorPage({ message: 'Network error', url: '/meta/channels' });
    }
  }, [channelsQuery.error]);

  // usage is fail-soft (non-critical): default Query error handling holds the
  // error, nothing routes it, and `usage` falls back to null — matching the old
  // silent-fail behavior.
  const usageQuery = useApiQueryWithTransport<UsageData>(
    qk.workspace(activeId ?? '', 'usage'),
    '/webhook/usage',
    wsTransport,
    { enabled: !!activeId && sessionScoped },
  );
  const usage = usageQuery.data ?? null;

  // orgUsage (spec D5): no workspace header; keyed on org. Distinct leaf
  // ('shell') from the billing usage meter's qk.org(orgId,'usage') so its
  // fail-soft error handling stays isolated from billing's transport — while a
  // prefix-invalidation on qk.org(orgId,'usage') (e.g. after a plan change)
  // still cascades here to refresh the threshold-toast pool.
  const orgPublicId = activeWorkspace?.organizationPublicId;
  const orgUsageQuery = useApiQueryWithTransport<OrgUsageData>(
    qk.org(orgPublicId ?? '', 'usage', 'shell'),
    `/organizations/${orgPublicId}/usage`,
    orgTransport,
    { enabled: isValidPublicId(orgPublicId, 'org') && sessionScoped },
  );
  const orgUsage = orgUsageQuery.data ?? null;

  // Phase 130 §4b — explicit demo-handoff claim. Owns URL→sessionStorage
  // capture + the POST /sandbox/claim-handoff once activeWorkspace +
  // sessionScoped resolve. Phase B sealed-session: hook uses cookie auth
  // directly; no getAccessToken dependency.
  useDemoHandoffClaim({
    apiUrl: API_URL,
    activeWorkspace,
    sessionScoped,
  });

  // Reset the high-water threshold when the active ORG changes (spec D5).
  // Keyed on organizationPublicId so switching workspaces within the same org
  // does NOT re-fire a toast the user already dismissed for that pool.
  useEffect(() => {
    lastNotifiedThreshold.current = 0;
  }, [activeWorkspace?.organizationPublicId]);

  // Threshold usage notifications driven by the org-pooled usage (spec D5).
  // Fires at most one toast per fetch: the first threshold the percentage
  // crosses that is above the current high-water mark.
  useEffect(() => {
    if (!orgUsage) return;
    const currentPct = orgUsage.percentage;

    for (const threshold of THRESHOLDS) {
      if (currentPct >= threshold && lastNotifiedThreshold.current < threshold) {
        toast.warning(`Usage at ${currentPct}%`, {
          description: `You've used ${orgUsage.total.toLocaleString()} of ${orgUsage.limit.toLocaleString()} ${orgUsage.usageUnit === 'actions' ? 'actions' : 'messages'} this quota period.`,
        });
        lastNotifiedThreshold.current = threshold;
        break; // Only show one toast per usage fetch
      }
    }
  }, [orgUsage]);

  // Phase 117: `id` is a branded `WorkspacePublicId` (`ws_...`). Writing it to
  // localStorage verbatim overrides any prior value — including a stale UUID
  // that pre-mount validation (readStoredWorkspaceId) may not have cleared yet
  // on the same tick as the caller.
  const switchWorkspace = useCallback(
    (id: WorkspacePublicId) => {
      setActiveId(id);
      // A customer is a drill-in, not your home scope: set it active in-memory
      // so its modules render, but DON'T persist it as last-active — else
      // RootRedirect would strand you inside a client next session (no hijack).
      if (workspaces.find((w) => w.id === id)?.kind === 'customer') return;
      localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
    },
    [workspaces],
  );

  // Refreshers now invalidate the Query cache (the query refetches while
  // enabled). Same signature the consumers already call.
  const refreshChannels = useCallback(async (workspaceId?: WorkspacePublicId) => {
    const targetId = workspaceId ?? activeId;
    if (targetId) {
      await queryClient.invalidateQueries({ queryKey: qk.workspace(targetId, 'channels') });
    }
  }, [activeId, queryClient]);

  const refreshUsage = useCallback(async () => {
    await Promise.all([
      activeId && queryClient.invalidateQueries({ queryKey: qk.workspace(activeId, 'usage') }),
      orgPublicId && queryClient.invalidateQueries({ queryKey: qk.org(orgPublicId, 'usage') }),
    ]);
  }, [activeId, orgPublicId, queryClient]);

  const refreshWorkspaces = useCallback(async () => {
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      activeWorkspace,
      workspaces: orgWorkspaces,
      crossOrgWorkspaces: workspaces,
      organizations,
      channels,
      usage,
      orgUsage,
      sessionScoped,
      switchWorkspace,
      refreshChannels,
      refreshWorkspaces,
      refreshUsage,
      loading,
      channelsLoading,
    }),
    [
      activeWorkspace,
      orgWorkspaces,
      workspaces,
      organizations,
      channels,
      usage,
      orgUsage,
      sessionScoped,
      switchWorkspace,
      refreshChannels,
      refreshWorkspaces,
      refreshUsage,
      loading,
      channelsLoading,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new UnexpectedError({
      message: 'useWorkspace must be used within a WorkspaceProvider',
      code: 'USE_WORKSPACE_OUTSIDE_PROVIDER',
    });
  }
  return context;
}
